import CancelButton from 'components/buttons/CancelButton';
import DeleteButton from 'components/buttons/DeleteButton';
import PermissionButton from 'components/buttons/PermissionButton';
import SaveButton from 'components/buttons/SaveButton';
import { useCreateDialogForm, useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { useCreateDraft } from 'components/create-dialog/useCreateDraft';
import { DraftSaveMode } from 'components/drafts/DraftSaveMode';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import CustomFields from 'components/forms/inputs/custom-fields/CustomFields';
import InfoTooltip from 'components/InfoTooltip';
import Loader from 'components/Loader';
import ConfirmNavigation from 'components/navigation/ConfirmNavigation';
import Section from 'components/section-menu/Section';
import { HighlightedFieldsProvider, SectionActionsProvider } from 'components/section-menu/SectionActionsContext';
import SectionLayout from 'components/section-menu/SectionLayout';
import { useSectionErrors } from 'components/section-menu/useSectionErrors';
import useFocusedEditMode from 'hooks/useFocusedEditMode';
import { useRef } from 'react';
import { Control, FieldValues, FormProvider } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'store';
import { useAddJobPositionMutation, useUpdateJobJobPositionMutation } from 'store/api/jobPositions';
import { ClassifierData } from 'types/classifiers';
import { CustomField } from 'types/customFields';
import { JobPositionCreateOrUpdateDto, JobPositionForm } from 'types/jobPositions';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useGoBack, useLeaveForm, useReturnToLocation } from 'utils/useNavigateWithReturnState';
import { JobPositionFields, JobPositionTranslationsFields } from './form-sections/JobPositionFields';
import { useJobPositionForm } from './form-sections/useJobPositionForm';
import { jobPositionFormToRequest } from './jobPositionFormMapping';
import { useDeleteJobPosition } from './useDeleteJobPosition';

type Props = {
    type: 'add' | 'edit';
    initialData?: JobPositionForm;
    classifiers: Record<string, ClassifierData>;
    refetch?: () => void;
    entityCustomFields?: CustomField[];
};

const FIELD_TO_SECTION_MAP: Record<string, string> = {
    name: 'job-positions.job-position',
    externalId: 'job-positions.job-position',
    positionTypeCode: 'job-positions.job-position',
    entityTexts: 'job-positions.position-name-translations',
};

const JobPositionPageForm = ({
    type = 'add',
    initialData,
    classifiers,
    refetch,
    entityCustomFields,
}: Props): JSX.Element => {
    useFocusedEditMode('job-positions');
    const { id: jobPositionId } = useCreateRoute().params;
    const returnTo = useReturnToLocation(jobPositionId ? `/job-position/${jobPositionId}` : '/job-positions');
    const goBack = useGoBack(returnTo);
    const leaveForm = useLeaveForm();

    const form = useJobPositionForm({ initialData, classifiers, entityCustomFields });
    const { methods, customFields, mapCustomFieldsToDto, isLoadingCustomFieldData } = form;
    useCreateDialogForm(methods);
    const draft = useCreateDraft({
        record: 'job-position',
        methods,
        toPayload: (data: JobPositionForm) =>
            jobPositionFormToRequest(data, mapCustomFieldsToDto(data as unknown as Record<string, unknown>)),
    });
    const { handleSubmit, control, reset, formState } = methods;
    const { errors } = formState;

    const sectionErrors = useSectionErrors(errors, FIELD_TO_SECTION_MAP);

    const [addJobPosition] = useAddJobPositionMutation();
    const [updateJobPosition] = useUpdateJobJobPositionMutation();

    const bypassNavigationBlockerRef = useRef(false);
    const handleDeleteJobPosition = useDeleteJobPosition(jobPositionId, () => {
        bypassNavigationBlockerRef.current = true;
        reset({}, { keepValues: true });
    });

    const onSubmit = async (formData: JobPositionCreateOrUpdateDto, action: 'create' | 'update'): Promise<void> => {
        try {
            if (action === 'create') {
                // A draft is created by completing it: the job position keeps the draft's id.
                await (draft.draftId === null ? addJobPosition(formData).unwrap() : draft.complete(formData))
                    .then((resp) => {
                        bypassNavigationBlockerRef.current = true;
                        reset({}, { keepValues: true });
                        leaveForm(`/job-position/${resp.id}`);
                    })
                    .catch(console.error);
            } else if (action === 'update' && jobPositionId && !isNaN(Number(jobPositionId))) {
                await updateJobPosition({
                    ...formData,
                    id: Number(jobPositionId),
                })
                    .unwrap()
                    .then(() => {
                        bypassNavigationBlockerRef.current = true;
                        if (refetch) refetch();
                        reset({}, { keepValues: true });
                        leaveForm(`/job-position/${jobPositionId}`);
                    })
                    .catch(console.error);
            }
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const submitCreate = () => {
        handleSubmit((data) =>
            onSubmit(
                jobPositionFormToRequest(data, mapCustomFieldsToDto(data as unknown as Record<string, unknown>)),
                'create'
            )
        )();
    };

    const submitUpdate = () => {
        handleSubmit((data) =>
            onSubmit(
                jobPositionFormToRequest(data, mapCustomFieldsToDto(data as unknown as Record<string, unknown>)),
                'update'
            )
        )();
    };

    const { buttonsDisabled } = useSelector((state) => state.buttons);

    if (isLoadingCustomFieldData) {
        return <Loader />;
    }

    // Show inital name if is edit form. Dont do dynamically to prevent unneeded rerenders
    const getFormTitle = () => {
        switch (type) {
            case 'edit':
                return initialData?.name;
            default:
                return t('job-positions.add-new-job-position');
        }
    };

    const getFormHeader = () => {
        const actions: JSX.Element[] = [<CancelButton key="cancel" onClick={goBack} />];

        if (type === 'add') {
            actions.push(
                ...(draft.button ? [draft.button] : []),
                <SaveButton
                    key="addSave"
                    permissions={['CREATE.JOB_POSITION']}
                    onClick={submitCreate}
                    disabled={buttonsDisabled}
                />
            );
        } else if (type === 'edit' && jobPositionId) {
            actions.push(
                <DeleteButton
                    key="editDeleteJobPosition"
                    permissions={['DELETE.JOB_POSITION']}
                    onClick={handleDeleteJobPosition}
                />,
                <PermissionButton
                    key="editSave"
                    permissions={['UPDATE.JOB_POSITION']}
                    color="secondary"
                    variant="contained"
                    size="small"
                    onClick={submitUpdate}
                    disabled={buttonsDisabled}
                >
                    <FormattedMessage id="save" />
                </PermissionButton>
            );
        }

        return <ActionHeader title={getFormTitle()} actions={actions} onBack={goBack} draft={draft.detail} />;
    };

    return (
        <FormProvider {...methods}>
            <SectionActionsProvider>
                <HighlightedFieldsProvider>
                    <FormWrapper>
                        {getFormHeader()}
                        <DraftSaveMode mode={draft.mode}>
                            <SectionLayout sectionErrors={sectionErrors}>
                                <Section title="job-positions.job-position">
                                    <JobPositionFields form={form} isExternalIdReadOnly={type === 'edit'} />
                                </Section>
                                <Section
                                    title="job-positions.position-name-translations"
                                    tooltip={<InfoTooltip text="translations.mandatory-fields" />}
                                >
                                    <JobPositionTranslationsFields form={form} />
                                </Section>
                                {customFields?.length > 0 ? (
                                    <CustomFields
                                        control={control as unknown as Control<FieldValues>}
                                        fields={customFields}
                                    />
                                ) : null}
                            </SectionLayout>
                        </DraftSaveMode>
                        <ConfirmNavigation
                            control={control}
                            bypassRef={bypassNavigationBlockerRef}
                            draft={type === 'add' ? draft.leave : null}
                        />
                    </FormWrapper>
                </HighlightedFieldsProvider>
            </SectionActionsProvider>
        </FormProvider>
    );
};

export default JobPositionPageForm;
